"use client"

import * as React from "react"
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "util/cn"
import { Button } from "./Button"

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: "horizontal" | "vertical"
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
  selectedIndex: number
  scrollSnaps: number[]
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />")
  }

  return context
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const resolvedOpts = React.useMemo(() => {
      const baseOpts: CarouselOptions = { ...(opts || {}) }
      const axis: "x" | "y" = orientation === "horizontal" ? "x" : "y"
      if (!baseOpts.loop && baseOpts.containScroll === undefined) {
        baseOpts.containScroll = "trimSnaps"
      }
      return {
        ...baseOpts,
        axis,
      }
    }, [opts, orientation])

    const [carouselRef, api] = useEmblaCarousel(
      resolvedOpts,
      plugins
    )
    const [canScrollPrev, setCanScrollPrev] = React.useState(false)
    const [canScrollNext, setCanScrollNext] = React.useState(false)
    const [selectedIndex, setSelectedIndex] = React.useState(0)
    const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([])

    const onSelect = React.useCallback((api: CarouselApi) => {
      if (!api) {
        return
      }

      setCanScrollPrev(api.canScrollPrev())
      setCanScrollNext(api.canScrollNext())
      setSelectedIndex(api.selectedScrollSnap())
    }, [])

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev()
    }, [api])

    const scrollNext = React.useCallback(() => {
      api?.scrollNext()
    }, [api])

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault()
          scrollPrev()
        } else if (event.key === "ArrowRight") {
          event.preventDefault()
          scrollNext()
        }
      },
      [scrollPrev, scrollNext]
    )

    React.useEffect(() => {
      if (!api || !setApi) {
        return
      }

      setApi(api)
    }, [api, setApi])

    React.useEffect(() => {
      if (!api) {
        return
      }

      onSelect(api)
      setScrollSnaps(api.scrollSnapList())
      api.on("reInit", onSelect)
      api.on("select", onSelect)

      return () => {
        api?.off("select", onSelect)
      }
    }, [api, onSelect])

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
          selectedIndex,
          scrollSnaps,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn("relative space-y-2", className)}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    )
  }
)
Carousel.displayName = "Carousel"

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div ref={carouselRef} className={cn("overflow-hidden", className)}>
      <div
        ref={ref}
        className={cn(
          "flex",
          orientation === "horizontal" ? "" : "-mt-0 flex-col",
          className
        )}
        {...props}
      />
    </div>
  )
})
CarouselContent.displayName = "CarouselContent"

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { orientation } = useCarousel()

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={cn(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "" : "pt-4",
        className
      )}
      {...props}
    />
  )
})
CarouselItem.displayName = "CarouselItem"

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { transparent?: boolean; hideWhenDisabled?: "hidden" | "invisible" }
>(({ className, variant = "default", size = "icon", transparent = false, hideWhenDisabled = "hidden", ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  // If hideWhenDisabled is "hidden" and can't scroll, don't render anything
  if (!canScrollPrev && hideWhenDisabled === "hidden") {
    return null
  }

  return (
    <div className={cn("group")}>
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "size-8 rounded-full z-[48]",
        !canScrollPrev && hideWhenDisabled === "invisible" && "invisible",
        transparent 
          ? "bg-white dark:bg-white bg-opacity-80 dark:bg-opacity-80 hover:bg-opacity-90 dark:hover:bg-opacity-90 border-none group-hover:bg-opacity-90 dark:group-hover:bg-opacity-90" 
          : "size-7",
        orientation === "horizontal"
          ? "relative"
          : "relative -top-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ChevronLeft className={cn("size-4", transparent ? "text-eoe-text-heading group-hover:text-black group-hover:dark:text-eoe-dark-text" : "text-eoe-text dark:text-eoe-dark-text")} />
      <span className="sr-only">Previous slide</span>
    </Button>
    </div>
  )
})
CarouselPrevious.displayName = "CarouselPrevious"

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { transparent?: boolean; hideWhenDisabled?: "hidden" | "invisible" }
>(({ className, variant = "default", size = "icon", transparent = false, hideWhenDisabled = "hidden", ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  // If hideWhenDisabled is "hidden" and can't scroll, don't render anything
  if (!canScrollNext && hideWhenDisabled === "hidden") {
    return null
  }

  return (
    <div className={cn("group")}>
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "size-8 rounded-full z-[48]",
        !canScrollNext && hideWhenDisabled === "invisible" && "invisible",
        transparent 
          ? "bg-white dark:bg-white bg-opacity-80 dark:bg-opacity-80 hover:bg-opacity-90 dark:hover:bg-opacity-90 border-none group-hover:bg-opacity-90 dark:group-hover:bg-opacity-90" 
          : "size-7",
        orientation === "horizontal"
          ? "relative"
          : "relative -bottom-12 left-1/2 -translate-x-1/2 rotate-90",
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ChevronRight className={cn("size-4", transparent ? "text-eoe-text-heading group-hover:text-black group-hover:dark:text-eoe-dark-text" : "text-eoe-text dark:text-eoe-dark-text")} />
      <span className="sr-only">Next slide</span>
    </Button>
    </div>
  )
})
CarouselNext.displayName = "CarouselNext"

const CarouselDots = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { api, selectedIndex, scrollSnaps } = useCarousel()

  const scrollTo = React.useCallback(
    (index: number) => {
      api?.scrollTo(index)
    },
    [api]
  )

  if (scrollSnaps.length <= 1) return null

  return (
    <div
      ref={ref}
      className={cn(
        "flex justify-center gap-1",
        className
      )}
      {...props}
    >
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          onClick={() => scrollTo(index)}
          className={cn(
            "size-2.5 rounded-full transition-all border-none",
            selectedIndex === index 
              ? "bg-eoe-gray dark:bg-white shadow-sm" 
              : "bg-eoe-gray/40 dark:bg-white/40 hover:bg-eoe-gray/60 dark:hover:bg-white/60"
          )}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  )
})
CarouselDots.displayName = "CarouselDots"

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CarouselDots,
}
